import { useEffect, useRef, useState } from "react";
import { getMapData, show3dMap } from "@mappedin/mappedin-js";
import { useMappedInSettings } from "../../hooks/useMappedInSettings";

// ─── Timing constants ────────────────────────────────────────────────────────
const LOGO_DURATION = 1000;
const STATIC_DURATION = 1000;
const PREVIEW_DURATION = 7000;

// Default fallback URL - will be replaced by dynamic mapLink from settings
const DEFAULT_BASE_URL = "https://web.mappedin.com/lga-laguardia/laguardia-airport.html";

// ─────────────────────────────────────────────────────────────────────────────
const MappedInMap = ({
  spaceName,
  externalId,
  floorLevel = "Level 5",
  locationName,
  brandColor,
  onError,
  videoDuration,
  autoStart = true,
  videoPlaying,
  videoData,
  userId,
  mapDelayTime = 2,
  totalSteps = 8,
  isVisible = false,
  stepDetails,
  logoBeforeMap,
  staticMapDuration, 
  currentLanguage = 'en',
}) => {
  // ── Refs ──────────────────────────────────────────────────────────────────
  const iframeRef = useRef(null);
  const logoTimerRef = useRef(null);
  const staticTimerRef = useRef(null);
  const previewTimerRef = useRef(null);
  const stepTimerRef = useRef(null);
  const isVisibleRef = useRef(isVisible);
  const navParamsRef = useRef(null);

  // KEY FIX: store BASE_URL in a ref so closures always read the latest value
  const baseUrlRef = useRef(DEFAULT_BASE_URL);

  useEffect(() => { isVisibleRef.current = isVisible; }, [isVisible]);

  // ── Phase state ───────────────────────────────────────────────────────────
  const [phase, setPhase] = useState("logo");

  // ── Config ────────────────────────────────────────────────────────────────
  const { settings: mappedInConfig, loading: configLoading } = useMappedInSettings(userId);

  const FROM_NODE = useRef();

  FROM_NODE.current = mappedInConfig?.mappedInStartLocation ;

  // const FROM_NODE = mappedInConfig?.mappedInStartLocation || "";

  const isConfigReady = !!mappedInConfig && !!FROM_NODE.current;

  console.log("From NNode deepak", FROM_NODE.current)



  // ── URL helpers (always use baseUrlRef.current) ───────────────────────────
  // const staticUrl  = (base) => `${base}#/directions?to=&from=${FROM_NODE}&lang=${currentLanguage}`;
  // const previewUrl = (base, to) => `${base}#/directions?to=${to}&from=${FROM_NODE}&lang=${currentLanguage}`;
  // const navUrl     = (base, to, instruction = 0) =>
  //   `${base}#/directions?to=${to}&from=${FROM_NODE}&textDirectionsVisible=true&instruction=${instruction}&lang=${currentLanguage}`;

  const staticUrl = (base) => {
    if (!FROM_NODE.current) return "";
    return `${base}#/directions?to=&from=${FROM_NODE.current}&lang=${currentLanguage}`;
  };

  const previewUrl = (base, to) => {
    if (!FROM_NODE.current) return "";
    return `${base}#/directions?to=${to}&from=${FROM_NODE.current}&lang=${currentLanguage}`;
  };

  const navUrl = (base, to, instruction = 0) => {
    if (!FROM_NODE.current) return "";
    return `${base}#/directions?to=${to}&from=${FROM_NODE.current}&textDirectionsVisible=true&instruction=${instruction}&lang=${currentLanguage}`;
  };

  // ── Update baseUrlRef whenever config loads ───────────────────────────────
  useEffect(() => {
    if (mappedInConfig?.mapLink) {
      const resolved = mappedInConfig.mapLink.split('#')[0].replace(/\/$/, '');
      baseUrlRef.current = resolved;
      console.log("BASE_URL resolved from config:", resolved);

      // If the iframe already loaded with the default URL, update it now
      if (iframeRef.current && phase === "logo") {
        iframeRef.current.src = staticUrl(resolved);
      }
    }
  }, [mappedInConfig]);

  // ── initMap ───────────────────────────────────────────────────────────────
  const initMap = async () => {
    try {
      const cfg = mappedInConfig;

      console.log("Config Map Id's", cfg)
      if (!cfg?.key || !cfg?.secret || !cfg?.mapId) return;

      const data = await getMapData({ key: cfg.key, secret: cfg.secret, mapId: cfg.mapId });
      const view = await show3dMap(document.getElementById("mappedin-map"), data);

      console.log("data", data);
      console.log("VIew", view);

      if (!externalId?.trim()) return;

      console.log("mappedInConfig", mappedInConfig)

      const startLocationExternalId = mappedInConfig?.mappedInStartLocation;
      const startLocationName = mappedInConfig?.mappedInStartLocationName;

      console.log("startLocation externalId and name", startLocationExternalId, startLocationName)

      const enterpriseLocations = data.getByType("enterprise-location");
      
      // Find start location by external ID or name
      let startLocation = null;
      
      if (startLocationExternalId) {
        // Try to find by external ID first
        startLocation = enterpriseLocations.find(
          (loc) => loc.externalId === startLocationExternalId || loc.name === startLocationName
        );
        console.log("Found start location by external ID:", startLocation?.name);
      }
      
      // Fallback to hardcoded locations if not found (for backward compatibility)
      if (!startLocation) {
        console.warn("Start location not configured in settings, using fallback");
        startLocation = enterpriseLocations.find(
          (loc) => loc.name === "LOC-1IP30ZO0" || loc.name === "Holographic Display"
        );
        if (startLocation) {
          console.log("Using fallback start location:", startLocation?.name);
        }
      }

      const endLocation = enterpriseLocations.find(
        (loc) => loc.externalId === externalId.trim()
      );

      if (!startLocation) { console.warn("Start location not found"); return; }
      if (!endLocation) { console.warn(`End location not found: ${externalId}`); return; }

      const directionsResult = await view.getDirections(startLocation, endLocation, { smoothing: true });
      if (!directionsResult) { console.warn("No directions found"); return; }

      navParamsRef.current = { to: externalId.trim(), directionsData: directionsResult };
      console.log("Nav params ready:", navParamsRef.current.to);
    } catch (err) {
      console.error("initMap error:", err);
    }
  };

  useEffect(() => {
    //  if (!isVisible || !isConfigReady) return;
    if (!configLoading && mappedInConfig) {
      initMap();
    }
    return () => { if (stepTimerRef.current) clearTimeout(stepTimerRef.current); };
  }, [configLoading, mappedInConfig]);

  // ── Step animation ────────────────────────────────────────────────────────
  const startStepAnimation = () => {
    const to = navParamsRef.current?.to;
    if (!to) return;

    const steps = stepDetails?.length
      ? stepDetails.map(step => ({
          ...step,
          // Round duration to 1 decimal place to avoid floating point issues
          duration: Math.round((step.duration || 5) * 10) / 10
        }))
      : Array.from({ length: totalSteps }, (_, i) => ({
        stepNumber: i + 1,
        duration: Math.max(Math.floor((videoDuration - 10000) / totalSteps / 100) / 10, 0.5),
      }));

    let index = 0;

    const showNextStep = () => {
      if (index >= steps.length) return;
      const step = steps[index];
      const instruction = step.stepNumber - 1;

      if (iframeRef.current && isVisibleRef.current) {
        // Always use the ref — never a stale closure value
        iframeRef.current.src = navUrl(baseUrlRef.current, to, instruction);
        console.log(`Step ${step.stepNumber} → instruction=${instruction}, duration=${step.duration}s`);
      }
      index += 1;
      // Convert duration to milliseconds and ensure it's an integer
      stepTimerRef.current = setTimeout(showNextStep, Math.floor(step.duration * 1000));
    };

    if (steps.length > 0) {
      index = 1;
      stepTimerRef.current = setTimeout(showNextStep, Math.floor(steps[0].duration * 1000));
    }
  };

  // ── Phase sequence — triggered when panel becomes visible ─────────────────
  useEffect(() => {
    if (!isVisible) return;

    logoTimerRef.current = setTimeout(() => {
      setPhase("static");

      staticTimerRef.current = setTimeout(() => {
        const to = navParamsRef.current?.to || externalId?.trim();
        if (!to) return;
        // Always read from ref — config is guaranteed loaded by now
        const base = baseUrlRef.current;

        if (iframeRef.current) iframeRef.current.src = previewUrl(base, to);
        setPhase("preview");

        previewTimerRef.current = setTimeout(() => {
          if (iframeRef.current) iframeRef.current.src = navUrl(base, to, 0);
          setPhase("nav");
          startStepAnimation();
        }, mapDelayTime * 1000 || PREVIEW_DURATION);

      }, STATIC_DURATION);
    }, logoBeforeMap?.displayDuration * 1000 || LOGO_DURATION);

    return () => {
      clearTimeout(logoTimerRef.current);
      clearTimeout(staticTimerRef.current);
      clearTimeout(previewTimerRef.current);
      clearTimeout(stepTimerRef.current);
    };
  }, [isVisible]);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>

      {/* ── Logo overlay ─────────────────────────────────────────────────── */}
      <div
        style={{
          position: "absolute", inset: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          backgroundColor: "#ffffff", borderRadius: "15px",
          zIndex: phase === "logo" ? 10 : -1,
          opacity: phase === "logo" ? 1 : 0,
          transition: "opacity 0.3s ease",
          pointerEvents: "none",
        }}
      >
        <img
          src={logoBeforeMap?.fileUrl || 'https://www.logoai.com/oss/icons/2021/10/27/MuCSnBxFpOQg2Kl.png'}
          alt="Loading..."
          style={{ width: logoBeforeMap?.width ? `${logoBeforeMap.width}%` : '100%', objectFit: "contain" }}
        />
      </div>

      {/* ── MappedIn iframe ───────────────────────────────────────────────── */}
      {/* Initial src uses DEFAULT_BASE_URL — updated to real URL once config loads */}
      <iframe
        ref={iframeRef}
        src={staticUrl(DEFAULT_BASE_URL)}
        onLoad={() => console.log("MappedIn iframe loaded")}
        style={{
          position: "absolute", inset: 0,
          width: "100%", height: "100%",
          border: "none", borderRadius: "15px",
          opacity: phase !== "logo" ? 1 : 0,
          transition: "opacity 0.4s ease",
          zIndex: 5,
          pointerEvents: phase !== "logo" ? "auto" : "none",
        }}
        title="MappedIn Map"
        allowFullScreen
      />
    </div>
  );
};

export default MappedInMap;
